import { QrCode, MapPin, Phone, Globe, Clock, Award, GraduationCap } from "lucide-react";

const INFO = "Information Required";

function InfoRequiredPill() {
    return (
        <span className="ddi-badge bg-orange-100 text-[#D97706]">
            Information Required
        </span>
    );
}

/** Live, non-editable preview of the doctor's public page — used in admin split view
 * and doctor final review. Not a real templating engine; a hard-coded template. */
export default function ProfilePreview({ doctor, profile, output, viewport = "desktop" }) {
    const width = viewport === "mobile" ? "max-w-[380px]" : viewport === "tablet" ? "max-w-[720px]" : "max-w-full";
    const p = profile || {};
    const o = output || {};

    const items = (arr) => arr || [];
    const bio = o.professional_biography;
    const summary = o.profile_summary;
    const specDesc = o.specialization_description;
    const philosophy = o.practice_philosophy;
    const mission = o.mission_vision;
    const timeline = items(o.career_timeline);
    const faqs = items(o.faqs);
    const headings = o.page_headings || {};

    const isInfo = (v) => v === INFO;

    return (
        <div className={"mx-auto ddi-card overflow-hidden " + width} data-testid="profile-preview">
            {/* Hero */}
            <div className="bg-[#123B5D] text-white p-6">
                <div className="flex items-start gap-4">
                    {p.profile_photo_url ? (
                        <img
                            src={p.profile_photo_url}
                            alt=""
                            className="h-16 w-16 rounded-full object-cover ring-2 ring-white/20"
                        />
                    ) : (
                        <div className="h-16 w-16 rounded-full bg-[#0F766E] flex items-center justify-center text-lg font-bold">
                            {(doctor?.full_name || "Dr").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-widest text-white/60">
                            {p.primary_specialization || doctor?.specialization || "Specialization"}
                        </div>
                        <div className="text-xl font-bold">{doctor?.full_name || "Dr. —"}</div>
                        <div className="text-xs text-white/60 mt-1">
                            Reg: {doctor?.medical_registration_number || "—"} · {doctor?.city || "—"}
                        </div>
                    </div>
                    <span className="ml-auto ddi-badge bg-teal-100 text-[#0F766E] shrink-0">
                        Preview
                    </span>
                </div>
                {isInfo(headings.home) ? (
                    <div className="mt-4"><InfoRequiredPill /></div>
                ) : headings.home ? (
                    <div className="mt-4 text-xl font-semibold">{headings.home}</div>
                ) : null}
            </div>

            <div className="p-6 space-y-6">
                {/* Summary */}
                <div>
                    <SectionTitle>{isInfo(headings.about) ? "About" : headings.about || "About"}</SectionTitle>
                    {isInfo(summary) ? <InfoRequiredPill /> : <div className="text-sm text-[#17212B]/85 leading-relaxed">{summary}</div>}
                </div>
                <div>
                    <SectionTitle>Biography</SectionTitle>
                    {isInfo(bio) ? <InfoRequiredPill /> : <div className="text-sm text-[#17212B]/85 leading-relaxed whitespace-pre-line">{bio}</div>}
                </div>
                <div>
                    <SectionTitle>{isInfo(headings.services) ? "Specialization" : headings.services || "Specialization"}</SectionTitle>
                    {isInfo(specDesc) ? <InfoRequiredPill /> : <div className="text-sm text-[#17212B]/85 leading-relaxed">{specDesc}</div>}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <SectionTitle>Practice philosophy</SectionTitle>
                        {isInfo(philosophy) ? <InfoRequiredPill /> : <div className="text-sm text-[#17212B]/85 leading-relaxed">{philosophy}</div>}
                    </div>
                    <div>
                        <SectionTitle>Mission &amp; vision</SectionTitle>
                        {isInfo(mission) ? <InfoRequiredPill /> : <div className="text-sm text-[#17212B]/85 leading-relaxed">{mission}</div>}
                    </div>
                </div>

                {/* Timeline */}
                <div>
                    <SectionTitle>Career timeline</SectionTitle>
                    {timeline.length === 0 ? (
                        <InfoRequiredPill />
                    ) : (
                        <ul className="text-sm space-y-1.5">
                            {timeline.map((t, i) => (
                                <li key={i} className="flex items-start gap-2">
                                    <Award size={14} className="text-[#0F766E] mt-0.5" />
                                    <span className="text-[#17212B]/85">
                                        <span className="font-mono text-[#123B5D]">{t.year}</span> — {t.event}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Consultation */}
                <div className="ddi-card p-4 bg-[#F5F8FA]">
                    <SectionTitle>{isInfo(headings.contact) ? "Consultation" : headings.contact || "Consultation"}</SectionTitle>
                    <div className="text-sm">
                        <div className="flex items-center gap-2 text-[#17212B]/70">
                            <Clock size={12} /> {p.consultation_hours?.length ? p.consultation_hours.map((h) => `${h.day} ${h.from}–${h.to}`).join(", ") : "—"}
                        </div>
                        <div className="mt-1 text-[#17212B]/70">
                            Base fee: ₹{p.base_fee_inr ?? "—"}{p.follow_up_fee_inr ? " · Follow-up ₹" + p.follow_up_fee_inr : ""}
                        </div>
                    </div>
                </div>

                {/* FAQs */}
                <div>
                    <SectionTitle>FAQs</SectionTitle>
                    {faqs.length === 0 ? (
                        <InfoRequiredPill />
                    ) : (
                        <ul className="text-sm space-y-3">
                            {faqs.map((f, i) => (
                                <li key={i}>
                                    <div className="font-semibold text-[#123B5D]">{f.q}</div>
                                    <div className="text-[#17212B]/80">{f.a}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* QR / share */}
                <div className="border-t border-[#123B5D]/10 pt-4 flex items-center gap-3">
                    <QrCode size={36} className="text-[#123B5D]" />
                    <div className="text-xs text-[#17212B]/60">
                        ddi.example/dr/{(doctor?.full_name || "your-name").toLowerCase().replaceAll(" ", "-")}
                    </div>
                </div>
            </div>
        </div>
    );
}

function SectionTitle({ children }) {
    return (
        <div className="text-[10px] uppercase tracking-widest text-[#0F766E] font-semibold mb-1.5">
            {children}
        </div>
    );
}
